export class QualificationCategoryType {
    readonly id: number;
    readonly description: string;

    constructor(id: number, description: string) {
        this.id = id;
        this.description = description;
    }

    public static Education = new QualificationCategoryType(1, "education");
    public static License = new QualificationCategoryType(2, "license/cert");
    public static LicenseWithState = new QualificationCategoryType(2,"license/cert (state specific)");
    public static Skill = new QualificationCategoryType(3, "skills");
    public static CustomQuestion = new QualificationCategoryType(7, "custom");
    
    public static asArray = () => [
        QualificationCategoryType.Education,
        QualificationCategoryType.License,
        QualificationCategoryType.LicenseWithState,
        QualificationCategoryType.Skill,
        QualificationCategoryType.CustomQuestion
    ];

    public static fromId = (id: number) =>
        QualificationCategoryType.asArray().find(i => i.id === id);
}

export class Qualification {
    readonly id: number;
    readonly qualificationCategory: QualificationCategoryType;
    readonly choiceTagTypeId: number;
    readonly contextTagTypeId: number;
    readonly question: string;
    readonly ordinal: number;

    constructor(
        id: number,
        ordinal: number,
        qualificationCategory: QualificationCategoryType,
        choiceTagTypeId: number,
        contextTagTypeId: number,
        question: string
    ) {
        this.id = id;
        this.ordinal = ordinal;
        this.qualificationCategory = qualificationCategory;
        this.choiceTagTypeId = choiceTagTypeId;
        this.contextTagTypeId = contextTagTypeId;
        this.question = question;
    }

    public static Education = new Qualification(
        10,
        1,
        QualificationCategoryType.Education,
        200,
        null,
        "What is the highest level of education you have completed?"
    );

    public static License = new Qualification(
        20,
        2,
        QualificationCategoryType.License,
        210,
        220,
        "Do you have a valid %blank% license/cert?"
    );

    public static LicenseAnyOf = new Qualification(
        25,
        3,
        QualificationCategoryType.License,
        210,
        220,
        "Do you have any of the following valid licenses/certs?"
    );

    public static LicenseWithState = new Qualification(
        30,
        4,
        QualificationCategoryType.LicenseWithState,
        210,
        220,
        "Do you have a valid %blank% license/cert in the state of %blank%?"
    );

    public static LicenseWithStateAnyOf = new Qualification(
        35,
        5,
        QualificationCategoryType.LicenseWithState,
        210,
        220,
        "Do you have any of the following valid licenses/certs in the state of %blank%?"
    );

    public static SkillAllOf = new Qualification(
        40,
        5,
        QualificationCategoryType.Skill,
        230,
        240,
        "How many years of experience do you have in %blank%?" // %blank% is a placeholder and will be replaced with ______ when rendered
    );

    public static SkillAnyOf = new Qualification(
        45,
        6,
        QualificationCategoryType.Skill,
        230,
        240,
        "Do you have at least %blank% years experience with any of the following?" // %blank% is a placeholder and will be replaced with ______ when rendered
    );

    public static CustomYesNo = new Qualification(
        80,
        7,
        QualificationCategoryType.CustomQuestion,
        280,
        null,
        "custom question with yes/no response"
    );

    public static CustomText = new Qualification(
        90,
        8,
        QualificationCategoryType.CustomQuestion,
        null,
        null,
        "custom question with text response"
    );


    public isEducation = () => this === Qualification.Education;
    public isLicense = () => this === Qualification.License;
    public isLicenseAnyOf = () => this === Qualification.LicenseAnyOf;
    public isLicenseWithState = () => this === Qualification.LicenseWithState;
    public isLicenseWithStateAnyOf = () =>
        this === Qualification.LicenseWithStateAnyOf;
    public isSkillAllOf = () => this === Qualification.SkillAllOf;
    public isSkillAnyOf = () => this === Qualification.SkillAnyOf;
    public isSkillAllOfAnyOf = () => this === Qualification.SkillAllOf || this === Qualification.SkillAnyOf;
    public isCustomYesNo = () => this === Qualification.CustomYesNo;
    public isCustomText = () => this === Qualification.CustomText;

    public static asArray = () => [
        Qualification.Education,
        Qualification.License,
        Qualification.LicenseAnyOf,
        Qualification.LicenseWithState,
        Qualification.LicenseWithStateAnyOf,
        Qualification.SkillAllOf,
        Qualification.SkillAnyOf,
        Qualification.CustomYesNo,
        Qualification.CustomText
    ];

    public static byQualificationCategory = (cat: QualificationCategoryType) =>
        Qualification.asArray().filter(q => q.qualificationCategory === cat);

    public static fromId = (id: number) =>
        Qualification.asArray().find(q => q.id === id);
}
